import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useSearch } from '@backstage/plugin-search-react';
import DOMPurify from 'dompurify';
import Autocomplete from '@mui/material/Autocomplete';
import { userSettingsApiRef } from '@mercedes-benz/user-settings-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  FEATURE_SEARCH_HISTORY,
  USER_PREFERENCES,
  UserSetting,
} from '@mercedes-benz/user-settings-common';
import { useDebouncedEffect } from '@react-hookz/web';

const MAX_NUMBER_SEARCH_HISTORY = 30;
const SEARCH_HISTORY_RECENT_LABEL = 'Recent';
const DEFAULT_DEBOUNCE = 700;

const useStyles = makeStyles(theme => ({
  textField: {
    borderRadius: '2px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.70)'
        : '1px solid rgba(0, 0, 0, 0.60)',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.02)',
  },
  icon: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.54)'
        : 'rgba(0, 0, 0, 0.54)',
  },
  cursor: { cursor: 'pointer' },
  enterKeyContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
    padding: '4px',
    borderRadius: '2px',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.1)',
    },
  },
}));

export type SearchbarType = {
  placeholder: string;
  handleSubmit?: (term: string) => void;
  searchHistoryBucket?: string;
  searchHistoryKey?: string;
  debounce?: number;
};

/*
 * This is a re-usable searchbar component with user search history.
 */
export const Searchbar = ({
  handleSubmit,
  placeholder,
  searchHistoryBucket,
  searchHistoryKey,
  debounce,
}: Readonly<SearchbarType>) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { term, setTerm } = useSearch();

  const [open, setOpen] = useState(false);
  const [displayTerm, setDisplayTerm] = useState<string>('');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [triggerSearch, setTriggerSearch] = useState<boolean>(false);

  const userSettingsApi = useApi(userSettingsApiRef);

  const fetchSearchHistory = useCallback(async () => {
    const result = (await userSettingsApi.get({
      bucket: USER_PREFERENCES,
      key: FEATURE_SEARCH_HISTORY,
    })) as UserSetting;

    const value = Array.isArray(result?.value)
      ? (result?.value as string[])
      : [];
    setSearchHistory(value);
    return value;
  }, [userSettingsApi]);

  const updateHistory = async (value: string) => {
    const searchedValues = await fetchSearchHistory();

    const reducedArray = searchedValues.filter(
      element => element.toLowerCase() !== value.toLowerCase(),
    );

    await userSettingsApi.set({
      bucket: searchHistoryBucket ?? '',
      key: searchHistoryKey ?? '',
      value: [value, ...reducedArray.slice(0, MAX_NUMBER_SEARCH_HISTORY - 1)],
    });
  };

  useEffect(() => {
    setDisplayTerm(term);
  }, [term]);

  useDebouncedEffect(
    () => {
      if (triggerSearch) {
        handleSubmit?.(displayTerm);
        setTerm(displayTerm);
        setTriggerSearch(false);
      }

      if (displayTerm && searchHistoryBucket && searchHistoryKey) {
        updateHistory(displayTerm);
      }

      setOpen(false);
    },
    [triggerSearch, displayTerm, term],
    debounce ?? DEFAULT_DEBOUNCE,
  );

  const sanitizeInput = (input: string): string => {
    return DOMPurify.sanitize(input.trim());
  };

  const handleSearchChange = (_: SyntheticEvent, value: string | null) => {
    const sanitizedValue = sanitizeInput(value ?? '');
    setTriggerSearch(true);

    if (!sanitizedValue) {
      setTerm('');
      setDisplayTerm('');
      return;
    }

    setDisplayTerm(sanitizedValue);
  };

  useEffect(() => {
    if (searchHistoryBucket && searchHistoryKey) {
      fetchSearchHistory();
    }
  }, [fetchSearchHistory, searchHistoryBucket, searchHistoryKey]);

  const handleKeyDown = (event: any) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }

    if (event.key === 'ArrowDown') {
      setOpen(true);
    }
  };

  return (
    <Autocomplete
      disablePortal
      fullWidth
      freeSolo
      open={open}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      forcePopupIcon={false}
      value={displayTerm}
      onKeyDown={handleKeyDown}
      options={[SEARCH_HISTORY_RECENT_LABEL, ...searchHistory]}
      onInputChange={handleSearchChange}
      onChange={(_, value, reason) => {
        if (reason === 'selectOption' && value) {
          setTerm(value);
          setOpen(false);
          handleSubmit?.(value);
        }
      }}
      ListboxProps={{
        style: {
          maxHeight: '220px',
        },
      }}
      getOptionDisabled={option => option === SEARCH_HISTORY_RECENT_LABEL}
      renderInput={params => (
        <TextField
          {...params}
          data-testid="reusable-search"
          placeholder={placeholder}
          variant="filled"
          fullWidth
          className={classes.textField}
          hiddenLabel
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="end">
                <SearchIcon className={classes.icon} />
              </InputAdornment>
            ),
            endAdornment: term && (
              <InputAdornment position="end">
                <CloseIcon
                  className={`${classes.icon} ${classes.cursor}`}
                  onClick={() => {
                    setDisplayTerm('');
                    setTerm('');
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
